import React, { useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { Crown, Check, ArrowRight } from "lucide-react";
import { useAuth } from "@/lib/auth";
import { Logo, ThemeToggle } from "@/components/sg/Brand";
import { PayButtons, isTrialEligible } from "@/components/sg/PayButtons";

const PERKS = [
  "Unlimited projects + AI milestone plans",
  "Change orders your client signs",
  "Your brand colour, no ScopeGuard branding",
  "Daily reminders until signed + late-fee note",
  "Alert the moment your client opens the link",
  "\u201cPay now\u201d button on portal & invoices",
];

export default function Welcome() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [annual, setAnnual] = useState(false);
  if (!isTrialEligible(user)) return <Navigate to="/app" replace />;
  const day7 = new Date(Date.now() + 7 * 864e5).toLocaleDateString(undefined, { month: "long", day: "numeric" });
  return (
    <div className="min-h-screen bg-background text-foreground" data-testid="welcome-page">
      <header className="max-w-5xl mx-auto px-6 py-4 flex items-center justify-between"><Logo to="/app" /><ThemeToggle /></header>
      <main className="max-w-5xl mx-auto px-6 py-10 grid md:grid-cols-2 gap-10 items-start">
        <div className="sg-rise">
          <div className="inline-flex items-center gap-2 rounded-full bg-amber-500/15 text-amber-700 dark:text-amber-400 px-3 py-1 text-xs font-bold tracking-widest uppercase">
            <Crown className="h-3.5 w-3.5" />Welcome gift
          </div>
          <h1 className="font-display text-4xl sm:text-5xl font-extrabold tracking-tight mt-5 leading-[1.05]">Your first 7 days of Super Premium are on us.</h1>
          <p className="text-muted-foreground mt-5 text-base leading-relaxed max-w-md">Try every top-tier tool on a real job. $0 today, and you'll get an email 2 days before the trial ends.</p>
          <ul className="mt-7 space-y-2.5 text-sm">
            {PERKS.map((p) => <li key={p} className="flex items-start gap-2"><Check className="h-4 w-4 text-emerald-600 dark:text-emerald-400 mt-0.5 flex-shrink-0" />{p}</li>)}
          </ul>
        </div>
        <div className="border border-border rounded-xl bg-card p-6 sg-rise" style={{ animationDelay: "120ms" }} data-testid="welcome-trial-card">
          <div className="grid grid-cols-2 gap-2 mb-5">
            {[[false, "$17.99", "/ month after trial"], [true, "$183.48", "/ year · save 15%"]].map(([a, price, sub]) => (
              <button key={String(a)} onClick={() => setAnnual(a)} data-testid={`welcome-${a ? "yearly" : "monthly"}-option`}
                className={`rounded-lg border p-3 text-left transition-colors ${annual === a ? "border-indigo-500 bg-indigo-600/10" : "border-border hover:border-indigo-500/50"}`}>
                <div className="font-display font-bold tabular-nums">{price}</div>
                <div className="text-[11px] text-muted-foreground">{sub}</div>
              </button>
            ))}
          </div>
          <div className="flex items-center justify-between text-sm border-y border-border py-3 mb-5">
            <span className="text-muted-foreground">Due today</span><span className="font-display font-bold text-lg" data-testid="welcome-due-today">$0.00</span>
          </div>
          <div className="text-xs text-muted-foreground mb-4">First charge on {day7} unless you cancel in Settings.</div>
          <PayButtons lookupKey={annual ? "super_premium_yearly" : "super_premium_monthly"} trial testPrefix="welcome" />
          <button data-testid="welcome-skip-btn" onClick={() => nav("/app")}
            className="mt-5 w-full text-sm text-muted-foreground hover:text-foreground transition-colors inline-flex items-center justify-center gap-1">
            Continue on the Free plan <ArrowRight className="h-3.5 w-3.5" />
          </button>
        </div>
      </main>
    </div>
  );
}
